import Observable from '../observable'
import BaseSource from '../sources/base_source'
import BaseLayer from '../layers/base_layer'
import BackgroundLayer from '../layers/background_layer'
import RasterLayer from '../layers/raster_layer'
import FillLayer from '../layers/fill_layer'
import LineLayer from '../layers/line_layer'
import FillExtrusionLayer from '../layers/fill_extrusion_layer'
import SymbolLayer from '../layers/symbol_layer'
import HeatmapLayer from '../layers/heatmap_layer'
import CircleLayer from '../layers/circle_layer'
import HillshadeLayer from '../layers/hillshade_layer'

import { diff } from 'deep-diff'
import {axios, BaseURL, debug} from '../common'
import {METADATA_POLYGON_EDITING, METADATA_POLYGON_SETTINGS} from '../constants'
import DEFAULT_METADATA from '../metadata'

export type PolygonIMDFSetting = {
  type: string;
  imdf: boolean;
  shrink: number;
}

export type PolygonIMDFSettings = {
  [id: string]: PolygonIMDFSetting;
}

export default class Style extends Observable {
  id: string
  organization_id: string
  version: number
  name: string
  metadata: any
  center: [number, number]
  zoom: number
  bearing: number
  pitch: number
  sources: { [id: string]: any }
  glyphs: string
  layers: BaseLayer[]
  overlay: boolean = false
  segments: boolean = false
  routable: boolean = false
  footprint: boolean = false
  cluster: boolean = true
  rooms: boolean = true
  fills: boolean = true
  showText: boolean = true
  floorSelection: boolean = false
  original: any;

  constructor(data:any) {
    super()
    this.original = data;
    this.id = data.id
    this.organization_id = data.organization_id
    this.version = data.version || 1
    this.name = data.name || 'New Style'
    this.metadata = Object.assign(DEFAULT_METADATA, data.metadata || {})
    this.center = data.center || [18.5550, 48.4437]
    this.zoom = data.zoom || 8
    this.bearing = data.bearing || 0
    this.pitch = data.pitch || 0
    this.sources = data.sources || []

    if (this.hasCustomTiles()) {
      const url = this.getTilesUrl()
      this.sources['simple-tiles-source'] = {
        type: 'raster',
        tiles: [ url ],
        tileSize: this.metadata['proximiio:raster:tilesize'] || 256
      }
    }

    this.glyphs = data.glyphs || ''
    this.layers = (data.layers || []).map((layer: any) => {
      if (layer.type === 'background') {
        return new BackgroundLayer(layer)
      }
      if (layer.type === 'raster') {
        return new RasterLayer(layer)
      }
      if (layer.type === 'fill') {
        return new FillLayer(layer)
      }
      if (layer.type === 'line') {
        return new LineLayer(layer)
      }
      if (layer.type === 'fill-extrusion') {
        return new FillExtrusionLayer(layer)
      }
      if (layer.type === 'symbol') {
        return new SymbolLayer(layer)
      }
      if (layer.type === 'heatmap') {
        return new HeatmapLayer(layer)
      }
      if (layer.type === 'circle') {
        return new CircleLayer(layer)
      }
      if (layer.type === 'hillshade') {
        return new HillshadeLayer(layer)
      }
      return new BaseLayer(layer)
    }).concat(this.getUniversalLayers('main'))
      .concat(this.getSyntheticLayers())
  }

  getTilesUrl(level: number = 0) {
    const matched = this.metadata['proximiio:raster:tileurl'].match(/proximiio-tiles/);
    const isProximiioCDN = matched && matched.length === 1;

    let url = isProximiioCDN ?
      this.metadata['proximiio:raster:tileurl'] :
      `${BaseURL}/imageproxy?source=${this.metadata['proximiio:raster:tileurl']}`

    url = this.isLeveled(url) ? url.replace(/{LEVEL}/g, level) : url;
    return url
  }

  isLeveled(url: string) {
    return url.match(/{LEVEL}/g) !== null
  }

  getUniversalLayers(source: string) {
    const filter = [
      "all",
      [ "==", [ "geometry-type" ], "Polygon" ],
      [ "any",
        [ "==", [ "get", "segment" ], false ],
        [ "!",
          [ "has", "segment" ]
        ],
      ],
      [ "any",
        [ "==", [ "get", "routable" ], false ],
        [ "!",
          [ "has", "routable" ]
        ]
      ],
      [ "==", [ "get", "level" ], 0 ]
    ]

    const segmentFilter = [
      "all",
      [ "==", [ "geometry-type" ], "Polygon" ],
      [ "has", "segment" ],
      [ "==", [ "get", "segment" ], true ],
      [ "==", [ "get", "level" ], 0 ]
    ]

    const routableFilter = [
      "all",
      [ "==", [ "geometry-type" ], "Polygon" ],
      [ "has", "routable" ],
      [ "==", [ "get", "routable" ], true ],
      [ "==", [ "get", "level" ], 0 ]
    ]

    const roomFilter = [
      "all",
      [ "==", [ "geometry-type" ], "Polygon" ],
      [ "has", "room" ],
      [ "==", [ "get", "room" ], true ],
      [ "==", [ "get", "level" ], 0 ]
    ]

    const directionsFilter = [
      "all",
      [ "==", [ "get", "class" ], "path" ],
      [ "==", [ "to-boolean", [ "has", "bidirectional" ] ], true ],
      [ "==", [ "to-boolean", [ "get", "bidirectional" ] ], false ],
      [ "==", [ "to-boolean", [ "get", "swapDirection" ] ], false ],
      [ "==", [ "get", "visibility" ], "visible" ],
      [ "==", [ "to-number", [ "get", "level" ] ], 0 ]
    ];

    const directionsOppositeFilter = [
      "all",
      [ "==", [ "get", "class" ], "path" ],
      [ "==", [ "to-boolean", [ "has", "bidirectional" ] ], true ],
      [ "==", [ "to-boolean", [ "get", "bidirectional" ] ], false ],
      [ "==", [ "to-boolean", [ "get", "swapDirection" ] ], true ],
      [ "==", [ "get", "visibility" ], "visible" ],
      [ "==", [ "to-number", [ "get", "level" ] ], 0 ]
    ];

    const unavailableFilter = [
      "all",
      [ "==", [ "get", "class" ], "path" ],
      [ "==", [ "to-boolean", [ "has", "available" ] ], true ],
      [ "==", [ "to-boolean", [ "get", "available" ] ], false ],
      [ "==", [ "get", "visibility" ], "visible" ],
      [ "==", [ "to-number", [ "get", "level" ] ], 0 ]
    ];

    const unavailablePoiFilter = [
      "all",
      [ "==", [ "get", "usecase" ], "poi" ],
      [ "==", [ "to-boolean", [ "has", "available" ] ], true ],
      [ "==", [ "to-boolean", [ "get", "available" ] ], false ],
      [ "==", [ "get", "visibility" ], "visible" ],
      [ "==", [ "to-number", [ "get", "level" ] ], 0 ]
    ];

    const inaccessiblePathFilter = [
      "all",
      [ "==", [ "get", "class" ], "path" ],
      [ "==", [ "get", "narrowPath" ], true ],
      [ "==", [ "get", "visibility" ], "visible" ],
      [ "==", [ "to-number", [ "get", "level" ] ], 0 ]
    ];

    const workingHoursFilter = [
      "all",
      [ "==", [ "get", "class" ], "path" ],
      [ "==", [ "to-boolean", [ "has", "_workingHoursEnabled" ] ], true ],
      [ "==", [ "to-boolean", [ "get", "_workingHoursEnabled" ] ], true ],
      [ "==", [ "get", "visibility" ], "visible" ],
      [ "==", [ "to-number", [ "get", "level" ] ], 0 ]
    ];

    const floorFilter = [
      "all",
      [ "==", [ "get", "type" ], "floor" ],
    ]

    const footprintFilter = [
      "all",
      [ "==", [ "get", "type" ], "footprint" ],
    ]

    const base = {
      minzoom: 1,
      maxzoom: 24,
      source
    }

    const layers = [
      new FillLayer({
        ...base,
        id: `${source}-polygon-fill`,
        type: 'fill',
        filter,
        layout: {
          "visibility": "none"
        },
        paint: {
          'fill-color': '#08c',
          'fill-opacity': 0.3
        }
      }),
      new LineLayer({
        ...base,
        id: `${source}-polygon-outline`,
        type: 'line',
        filter,
        layout: {
          "line-join": "bevel",
          "visibility": "none"
        },
        paint: {
          'line-color': '#08c',
          'line-width': 1,
          'line-opacity': 1
        }
      }),
      new LineLayer({
        ...base,
        id: `${source}-room-outline`,
        type: 'line',
        roomFilter,
        layout: {
          "line-join": "bevel",
          "visibility": "none"
        },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 1,
          'line-opacity': 1
        }
      }),
      new FillLayer({
        ...base,
        id: `${source}-room-fill`,
        type: 'fill',
        filter: roomFilter,
        layout: {
          "visibility": "none"
        },
        paint: {
          'fill-color': ['get', 'color'],
          'fill-opacity': 0.3
        }
      }),
      // segments
      new FillLayer({
        ...base,
        id: `${source}-segment-fill`,
        type: 'fill',
        filter: segmentFilter,
        layout: {
          "visibility": "none"
        },
        paint: {
          'fill-color': '#0c8',
          'fill-opacity': 0.3
        }
      }),
      new LineLayer({
        ...base,
        id: `${source}-segment-outline`,
        type: 'line',
        filter: segmentFilter,
        layout: {
          "line-join": "bevel",
          "visibility": "none"
        },
        paint: {
          'line-color': '#0c8',
          'line-width': 1,
          'line-opacity': 1
        }
      }),
      // routables
      new FillLayer({
        ...base,
        id: `${source}-routable-fill`,
        type: 'fill',
        filter: routableFilter,
        layout: {
          "visibility": "none"
        },
        paint: {
          'fill-color': '#c80',
          'fill-opacity': 0.3
        }
      }),
      new LineLayer({
        ...base,
        id: `${source}-routable-outline`,
        type: 'line',
        filter: routableFilter,
        layout: {
          "line-join": "bevel",
          "visibility": "none"
        },
        paint: {
          'line-color': '#c80',
          'line-width': 1,
          'line-opacity': 1
        }
      }),
      new LineLayer({
        ...base,
        id: `${source}-inaccessible`,
        type: 'line',
        filter: inaccessiblePathFilter,
        layout: {
          "line-join": "bevel",
          "visibility": "visible"
        },
        paint: {
          "line-opacity": 1,
          "line-color": "#0080c0",
          "line-width": 8,
          "line-dasharray": [
            0.5,
            1
          ]
        }
      }),
      new FillLayer({
        ...base,
        id: `${source}-footprint`,
        type: 'fill',
        filter: footprintFilter,
        layout: {
          "visibility": "none"
        },
        paint: {
          'fill-color': '#bbf',
          'fill-opacity': 0.7
        }
      }),
    ] as BaseLayer[];

    layers.push(
      new SymbolLayer({
        ...base,
        id: `${source}-arrow-right`,
        type: 'symbol',
        minzoom: 9,
        maxzoom: 24,
        filter: directionsFilter,
        layout: {
          "icon-image": "arrow_right",
          "icon-size": 0.35,
          "symbol-placement": "line",
          "symbol-spacing": 40,
          "icon-rotation-alignment": "map"
        }
      }),
      new SymbolLayer({
        ...base,
        id: `${source}-arrow-left`,
        type: 'symbol',
        minzoom: 9,
        maxzoom: 24,
        filter: directionsOppositeFilter,
        layout: {
          "icon-image": "arrow_left",
          "icon-size": 0.35,
          "symbol-placement": "line",
          "symbol-spacing": 40,
          "icon-rotation-alignment": "map"
        }
      }),
      new SymbolLayer({
        ...base,
        id: `${source}-unavailable`,
        type: 'symbol',
        minzoom: 9,
        maxzoom: 24,
        filter: unavailableFilter,
        layout: {
          "icon-image": "disabled",
          "icon-size": 0.5,
          "symbol-placement": "line",
          "symbol-spacing": 40,
          "icon-rotation-alignment": "map"
        }
      }),
      new SymbolLayer({
        ...base,
        id: `${source}-poi-unavailable`,
        type: 'symbol',
        minzoom: 9,
        maxzoom: 24,
        filter: unavailablePoiFilter,
        layout: {
          "icon-image": "disabled",
          "icon-size": 1.0,
          "symbol-placement": "line",
          "symbol-spacing": 40,
          "icon-rotation-alignment": "map"
        }
      }),
      new SymbolLayer({
        ...base,
        id: `${source}-working-hours`,
        type: 'symbol',
        minzoom: 9,
        maxzoom: 24,
        filter: workingHoursFilter,
        layout: {
          "icon-image": "working-hours",
          "icon-size": 0.75,
          "symbol-placement": "line",
          "symbol-spacing": 40,
          "icon-rotation-alignment": "map"
        }
      })
    )

    return layers;
  }

  getSyntheticLayers() {
    const polygonFill = new FillLayer({
      type: 'fill',
      minzoom: 1,
      maxzoom: 24,
      source: 'synthetic',
      id: 'synthetic-polygon-fill',
      filter: [
        "all",
        [
          "==",
          [
            "geometry-type"
          ],
          "Polygon"
        ]
      ],
      layout: {
        "visibility": "visible"
      },
      paint: {
        'fill-color': '#08c',
        'fill-opacity': 0.3
      }
    })

    const polygonOutline = new LineLayer({
      type: 'line',
      minzoom: 1,
      maxzoom: 24,
      source: 'synthetic',
      id: 'synthetic-polygon-outline',
      filter: [
        "all",
        [
          "==",
          [
            "geometry-type"
          ],
          "Polygon"
        ]
      ],
      layout: {
        "line-join": "bevel",
        "visibility": "visible"
      },
      paint: {
        'line-color': '#08c',
        'line-width': 1,
        'line-opacity': 1
      }
    });

    return [
      polygonFill,
      polygonOutline,
    ]
  }

  async addFloorLayer() {
    const firstProximiLayerIdx = this.layers.findIndex(layer => layer.id.includes('proximiio'));
    const floorLayer = {
      "id": "proximiio-floors",
      "minzoom": 12,
      "maxzoom": 24,
      "type": "fill",
      "source": "main",
      "filter": [
        "all",
        [ "==", [ "get", "type" ], "floor" ],
        [ "==", [ "get",  "category" ], "base_floor" ],
        [ "==", [ "to-number", [ "get", "level" ]], 0]
      ],
      "paint": {
        "fill-color": "#FFFFFF",
        "fill-opacity": 0.7
      }
    };

    const style = Object.assign({}, this.original);
    style.layers.splice(firstProximiLayerIdx + 1, 0, floorLayer);
    await axios.post('/v7/geo/style', style);
    const layer = new FillLayer(floorLayer);
    this.notify('layer-add', layer);
  }

  usesPrefixes() {
    return typeof this.layers.find(layer => layer.id === 'proximiio-paths') !== 'undefined'
  }

  getLayer(id: string) {
    return this.layers.find(layer => layer.id === id)
  }

  getLayerIndex(id: string) {
    return this.layers.findIndex(layer => layer.id === id)
  }

  getLayers(source_id: string): BaseLayer[] {
    return this.layers.filter(layer => layer.source && layer.source === source_id)
  }

  getSources(): BaseSource[] {
    const sources = [] as BaseSource[]
    Object.keys(this.sources).forEach(id => {
      const source = this.sources[id]
      source.id = id
      sources.push(source)
    })
    return sources
  }

  hasCustomTiles = () =>
    typeof this.metadata['proximiio:raster:tileurl'] !== 'undefined' &&
    this.metadata['proximiio:raster:tileurl'].length > 0;

  setSource(id: string, data: BaseSource) {
    this.sources[id] = data.source
  }

  setLevel(level: number) {
    [...this.getLayers('main'), ...this.getLayers('route')].forEach(layer => {
      if (!layer.filter) {
        return
      }
      layer.setFilterLevel(level)
    });

    // if (this.hasCustomTiles()) {
    //   // this.sources['simple-tiles-source'] = {
    //   //   type: 'raster',
    //   //   tiles: [ url ],
    //   //   tileSize: this.metadata['proximiio:raster:tilesize'] || 256
    //   // }
    // }
    this.notify('filter-change')
  }

  get hasFloorLayer() {
    return this.layers.findIndex(l => l.id === 'proximiio-floors') > 0
  }

  get polygonEditing() {
    return this.metadata[METADATA_POLYGON_EDITING] || false
  }

  get lineTypes() {
    const types = ['default'] as string[];
    this.layers.filter(f => f.type === 'line').forEach(layer => {
      if (Array.isArray(layer.filter)) {
        layer.filter.forEach((subfilter: unknown) => {
          if (Array.isArray(subfilter) && Array.isArray(subfilter[1]) && subfilter[1].length === 2 && subfilter[1][1] === 'type') {
            if (!types.includes(subfilter[2])) {
              types.push(subfilter[2]);
            }
          }
        })
      }
    });

    return types
  }

  get polygonTypes() {
    const types = [] as string[];
    const allowed = ['fill', 'fill-extrusion'];
    this.layers.filter(f => allowed.includes(f.type)).forEach(layer => {
      if (Array.isArray(layer.filter)) {
        layer.filter.forEach((subfilter: unknown) => {
          if (Array.isArray(subfilter) && Array.isArray(subfilter[1]) && subfilter[1].length === 2 && subfilter[1][1] === 'type') {
            const type = subfilter[2];
            if (!types.includes(type)) {
              types.push(subfilter[2]);
            }
          }
        })
      }
    });
    if (!types.includes('floor')) {
      types.push('floor');
    }

    if (!types.includes('footprint')) {
      types.push('footprint');
    }

    types.sort();
    return types
  }

  get polygonTypesSettings() {
    const types = this.polygonTypes;
    const settings = this.metadata[METADATA_POLYGON_SETTINGS] as PolygonIMDFSettings|| {} as PolygonIMDFSettings;

    types.forEach(type => {
      if (!settings[type]) {
        settings[type] = { type, imdf: false, shrink: 0}
      }
    });

    return settings
  }

  updatePolygonTypeSetting(settings: PolygonIMDFSettings) {
    this.metadata[METADATA_POLYGON_SETTINGS] = settings;
    this.notify('metadata-update')
  }

  toggleCluster() {
    this.cluster = !this.cluster;
    this.notify('cluster-toggled')
  }

  toggleShowText() {
    this.showText = !this.showText;
    this.notify('show-text-toggled')
  }

  togglePolygonEditing() {
    if (this.metadata[METADATA_POLYGON_EDITING]) {
      this.metadata[METADATA_POLYGON_EDITING] = !this.metadata[METADATA_POLYGON_EDITING]
    } else {
      this.metadata[METADATA_POLYGON_EDITING] = true
    }

    this.notify('polygon-editing-toggled')
  }

  toggleRooms() {
    this.rooms = !this.rooms;
    this.notify('rooms-toggled')
  }

  toggleOverlay() {
    this.overlay = !this.overlay;
    this.notify('overlay-toggled')
  }

  toggleSegments() {
    this.segments = !this.segments;
    this.notify('segments-toggled')
  }

  toggleRoutable() {
    this.routable = !this.routable;
    this.notify('routable-toggled')
  }

  toggleFootprint() {
    this.footprint = !this.footprint;
    this.notify('footprint-toggled')
  }

  togglePaths(enabled: boolean) {
    const layer = this.layers.find(layer => layer.id === 'proximiio-paths' || layer.id === 'paths')
    if (layer) {
      const updated = new LineLayer(Object.assign({}, layer.json))
      updated.layout.visibility = enabled ? 'visible' : 'none'
      this.updateLayer(updated)
    }
  }

  updateLayer(layer: BaseLayer) {
    // debug.log('style updateLayer in:', layer)
    const idx = this.getLayerIndex(layer.id)
    let changes = [] as any
    if (idx >= 0) {
      const prev = this.layers[idx]
      const change = diff(prev.json, layer.json)
      debug.log('change', change)
      if (change) {
        changes.push(change)
        this.layers.splice(idx, 1, layer)
        // debug.log('style should update layer', layer, 'changes', changes)
        this.notify('layer-update', { layer, changes: changes.length > 0 ? changes[0] : [] })
      }
    }
  }

  setMetadata(namespace: string, item: string, value: any) {
    this.metadata[`${namespace}:${item}`] = value
    this.notify('metadata-update')
  }

  get namespaces(): string[] {
    const keys = Object.keys(this.metadata)
    const pairs = keys.filter(key => key.indexOf(':') > 0)
    const unique = new Set(pairs.map(pair => pair.split(':')[0]))
    return Array.from(unique).sort((a, b) => a.localeCompare(b))
  }

  namespaceItems(namespace: string) {
    return Object.keys(this.metadata)
      .filter(key => key.indexOf(`${namespace}:`) === 0)
      .map(key => (key.split(':').slice(1)).join(':'))
  }

  toggleFills() {
    this.fills = !this.fills;
    this.notify('fills-toggled')
  }

  get minPoiZoom(): number {
    return this.metadata['proximiio:poi:minzoom'] ?? 10
  }

  get maxPoiZoom(): number {
    return this.metadata['proximiio:poi:maxzoom'] ?? 24
  }

  get json(): any {
    const style = Object.assign({}, this) as any;
    delete style._observers;
    delete style.overlay;
    style.layers = this.layers.map(layer => layer.json)
    return JSON.parse(JSON.stringify(style))
  }
}
